import { useState } from "react";
import { User, AtSign, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterForm = ({ onCodeSent, onSwitchToLogin }) => {
  const [name, setName] = useState("");
  const [userTag, setUserTag] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleUserTagChange = (e) => {
    setUserTag(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    if (!userTag.trim()) { setError("User tag is required"); return; }
    if (userTag.trim().length < 3) { setError("User tag must be at least 3 characters"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }

    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/send-code`, {
        email, password, name, user_tag: userTag.trim()
      });
      onCodeSent({ code: data.code, email });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h2 className="text-3xl font-black mb-2" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
        Join Us
      </h2>
      <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>Create your account and pick a unique user tag</p>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="register-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Full Name</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe"
              className="pl-10 rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="auth-name-input" required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>User Tag</label>
          <div className="relative">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type="text" value={userTag} onChange={handleUserTagChange} placeholder="JohnDoe"
              className="pl-10 rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="auth-usertag-input" required maxLength={20} />
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--color-text-dim)" }}>
            Displayed on your comments as @{userTag || "yourtag"}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="pl-10 rounded-lg" style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="auth-email-input" required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters" className="pl-10 pr-10 rounded-lg"
              style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="auth-password-input" required minLength={6} />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: "var(--color-text-dim)" }} data-testid="toggle-password">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-lg py-3 font-bold text-white"
          style={{ backgroundColor: "var(--color-accent)" }} data-testid="auth-submit-button">
          {loading ? "Please wait..." : "Continue"}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <button onClick={onSwitchToLogin} className="text-sm font-medium" style={{ color: "var(--color-accent)" }} data-testid="auth-toggle-button">
          Already have an account? Sign in
        </button>
      </div>
    </>
  );
};

export default RegisterForm;
